import * as React from "react";
import {
  Circle,
  CircleCheck,
  CircleX,
  Info,
  TriangleAlert,
  type LucideIcon,
} from "lucide-react";
import { mergeClassNames } from "./class-names";

export type StatusBadgeStatus =
  "neutral" | "success" | "warning" | "error" | "info";

interface StatusBadgeDefinition {
  readonly className: string;
  readonly icon: LucideIcon;
}

const statusBadgeDefinitions = {
  neutral: {
    className: "bg-subtle text-secondary",
    icon: Circle,
  },
  success: {
    className: "bg-success-subtle text-success",
    icon: CircleCheck,
  },
  warning: {
    className: "bg-warning-subtle text-warning",
    icon: TriangleAlert,
  },
  error: {
    className: "bg-danger-subtle text-danger",
    icon: CircleX,
  },
  info: {
    className: "bg-info-subtle text-info",
    icon: Info,
  },
} satisfies Record<StatusBadgeStatus, StatusBadgeDefinition>;

export interface StatusBadgeProps extends React.ComponentPropsWithoutRef<"span"> {
  readonly status?: StatusBadgeStatus;
}

export const StatusBadge = React.forwardRef<HTMLSpanElement, StatusBadgeProps>(
  ({ children, className, status = "neutral", ...props }, ref) => {
    const definition = statusBadgeDefinitions[status];
    const Icon = definition.icon;

    return (
      <span
        ref={ref}
        data-slot="status-badge"
        data-status={status}
        className={mergeClassNames(
          "text-body-small inline-flex max-w-full items-center gap-1.5 rounded-full px-2 py-1 font-semibold",
          definition.className,
          className,
        )}
        {...props}
      >
        <Icon className="size-3.5 shrink-0" aria-hidden="true" />
        <span className="min-w-0 break-words [overflow-wrap:anywhere]">
          {children}
        </span>
      </span>
    );
  },
);
StatusBadge.displayName = "StatusBadge";
